import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CreditCard, Calendar, Download, CheckCircle, XCircle,
  Clock, AlertCircle, RefreshCw, Crown, Zap, Star, FileText,
  ChevronRight, Loader2, X, Building2, Mail, Receipt, Menu, LogOut,
  LayoutDashboard, Briefcase, ClipboardList, Trophy, UserPlus, Search, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanySubscriptionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    const company = localStorage.getItem('company_data');
    if (company) {
      setCompanyData(JSON.parse(company));
    }
    
    fetchSubscriptionDetails();
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
    toast.success('Logged out successfully');
  };

  // Navigation items for sidebar
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard' },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs' },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges' },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events' },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics' },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription', active: true },
  ];

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/company/subscription/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('company_token');
        navigate('/company/auth');
      }
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (orderId) => {
    try {
      const res = await axios.get(`${API}/api/company/subscription/invoice/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInvoice(res.data);
      setShowInvoiceModal(true);
    } catch (err) {
      toast.error('Failed to load invoice');
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await axios.post(
        `${API}/api/company/subscription/cancel?reason=${encodeURIComponent(cancelReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription cancellation requested');
      setShowCancelModal(false);
      setCancelReason('');
      fetchSubscriptionDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setProcessing(true);
    try {
      await axios.post(
        `${API}/api/company/subscription/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription reactivated!');
      fetchSubscriptionDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'superseded': return 'bg-gray-100 text-gray-600';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Zap className="text-yellow-500" size={20} />;
      case 'professional': return <Star className="text-purple-500" size={20} />;
      case 'premium': return <Crown className="text-amber-500" size={20} />;
      default: return <CreditCard className="text-gray-500" size={20} />;
    }
  };

  const printInvoice = () => {
    const printContent = document.getElementById('invoice-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - ${selectedInvoice?.invoice_number}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .company-name { font-size: 24px; font-weight: bold; color: #7c3aed; }
            .invoice-title { font-size: 32px; font-weight: bold; color: #111; }
            .invoice-number { color: #666; margin-top: 8px; }
            .section { margin: 24px 0; padding: 16px; background: #f9fafb; border-radius: 8px; }
            .section-title { font-weight: 600; margin-bottom: 12px; color: #374151; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
            .row:last-child { border-bottom: none; }
            .total-row { font-weight: bold; font-size: 18px; margin-top: 16px; padding-top: 16px; border-top: 2px solid #111; }
            .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .status-active { background: #dcfce7; color: #166534; }
            .footer { margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">
            <p>This is a computer-generated invoice and does not require a signature.</p>
            <p>For queries, contact support@evalentx.com</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#666666]">Loading Subscription...</span>
        </div>
      </div>
    );
  }

  const activeSub = data?.active_subscription;
  const isCancelled = activeSub?.cancellation_requested;

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-out
        lg:transform-none h-screen overflow-y-auto
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -mr-2"
            data-testid="close-sidebar-btn"
          >
            <X size={20} className="text-[#666666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${item.active 
                    ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' 
                    : 'text-[#666666] hover:bg-gray-100 hover:text-[#191919]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg transition-colors"
            data-testid="company-logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
              data-testid="open-sidebar-btn"
            >
              <Menu size={22} className="text-[#666666]" />
            </button>
            <div>
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider hidden sm:block">Account</p>
              <h1 className="text-lg sm:text-xl font-semibold text-[#191919]">Subscription & Billing</h1>
            </div>
          </div>
          
          <Link
            to="/company/assessments/create"
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0a66c2] hover:bg-[#004182] text-white rounded-full text-sm font-medium transition-colors"
            data-testid="upgrade-plan-btn"
          >
            <Zap size={16} />
            <span className="hidden md:inline">Upgrade Plan</span>
          </Link>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        {/* Current Plan Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center ${
                  activeSub ? 'bg-[#e8f4f8]' : 'bg-gray-100'
                }`}>
                  {activeSub ? getPlanIcon(activeSub.plan_id) : <CreditCard className="text-gray-400" size={24} />}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg sm:text-xl font-semibold text-[#191919]">
                      {activeSub?.plan_name || 'No Active Plan'}
                    </h2>
                    {activeSub && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        isCancelled ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {isCancelled ? 'Cancelling' : 'Active'}
                      </span>
                    )}
                  </div>
                  {activeSub ? (
                    <p className="text-sm text-gray-600 mt-1">
                      {formatCurrency(activeSub.price || activeSub.amount)} / {activeSub.validity_days || 30} days
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1">
                      Subscribe to a plan to create assessments
                    </p>
                  )}
                </div>
              </div>
              
              {activeSub && !isCancelled && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Cancel Subscription
                </button>
              )}
              {isCancelled && (
                <button
                  onClick={handleReactivate}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Reactivate'}
                </button>
              )}
            </div>

            {/* Subscription Details */}
            {activeSub && (
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Calendar size={16} />
                    <span className="text-xs font-medium uppercase">Started</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {formatDate(activeSub.start_date || activeSub.starts_at)}
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Clock size={16} />
                    <span className="text-xs font-medium uppercase">{isCancelled ? 'Active Until' : 'Renews'}</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {formatDate(activeSub.end_date || activeSub.expires_at)}
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <FileText size={16} />
                    <span className="text-xs font-medium uppercase">Assessments Used</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {data?.usage_stats?.assessments_created || 0}
                    <span className="text-gray-400 font-normal">
                      /{data?.usage_stats?.assessments_limit === -1 ? '∞' : data?.usage_stats?.assessments_limit}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Cancellation Notice */}
            {isCancelled && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-medium text-amber-800">Cancellation Scheduled</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Your subscription will remain active until {formatDate(activeSub.end_date || activeSub.expires_at)}.
                    After that, you'll be moved to the free tier.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Billing History */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Billing History</h3>
            <p className="text-sm text-gray-500 mt-1">View and download invoices for past payments</p>
          </div>
          
          {data?.billing_history?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {data.billing_history.map((item, idx) => (
                <div key={item.order_id || idx} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        {getPlanIcon(item.plan_id)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.plan_name}</p>
                        <p className="text-sm text-gray-500">{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(item.price || item.amount)}
                        </p>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                        </span>
                      </div>
                      
                      {item.status === 'active' && (
                        <button
                          onClick={() => handleViewInvoice(item.order_id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
                          title="View Invoice"
                        >
                          <Receipt size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Receipt className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="text-gray-500">No billing history yet</p>
              <Link 
                to="/company/assessments/create"
                className="text-purple-600 hover:text-purple-700 text-sm font-medium mt-2 inline-block"
              >
                Subscribe to a plan
              </Link>
            </div>
          )}
        </div>

        {/* Company Info */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Billing Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Building2 className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500 uppercase">Company Name</p>
                <p className="font-medium text-gray-900">{data?.company?.name || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500 uppercase">Billing Email</p>
                <p className="font-medium text-gray-900">{data?.company?.email || '-'}</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Subscription</h3>
              <button onClick={() => setShowCancelModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                Your subscription will remain active until the end of your current billing period.
                You won't be charged again after cancellation.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Why are you cancelling? (Required)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Please let us know why you're leaving..."
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={processing || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Invoice</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={printInvoice}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button onClick={() => setShowInvoiceModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>
            
            <div id="invoice-content" className="p-6">
              {/* Invoice Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-purple-600">EvalentX</h2>
                  <p className="text-sm text-gray-500 mt-1">Assessment Platform</p>
                </div>
                <div className="text-right">
                  <h3 className="text-3xl font-bold text-gray-900">INVOICE</h3>
                  <p className="text-gray-500 mt-1">{selectedInvoice.invoice_number}</p>
                </div>
              </div>

              {/* Bill To & Invoice Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Bill To</h4>
                  <p className="font-semibold text-gray-900">{selectedInvoice.company?.name}</p>
                  <p className="text-gray-600">{selectedInvoice.company?.email}</p>
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">Invoice Date:</span>
                    <span className="ml-2 font-medium">{formatDate(selectedInvoice.dates?.invoice_date)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-xs text-gray-500">Period:</span>
                    <span className="ml-2 font-medium">
                      {formatDate(selectedInvoice.dates?.start_date)} - {formatDate(selectedInvoice.dates?.end_date)}
                    </span>
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedInvoice.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedInvoice.status === 'active' ? 'PAID' : selectedInvoice.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-100">
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{selectedInvoice.plan?.name} Plan</p>
                        <p className="text-sm text-gray-500">{selectedInvoice.plan?.validity_days} days subscription</p>
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-gray-900">
                        {formatCurrency(selectedInvoice.billing?.base_amount)}
                      </td>
                    </tr>
                    <tr className="border-t border-gray-100 bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">GST ({selectedInvoice.billing?.gst_rate}%)</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {formatCurrency(selectedInvoice.billing?.gst_amount)}
                      </td>
                    </tr>
                    <tr className="border-t-2 border-gray-900">
                      <td className="px-4 py-4 font-bold text-gray-900">Total</td>
                      <td className="px-4 py-4 text-right font-bold text-xl text-gray-900">
                        {formatCurrency(selectedInvoice.billing?.total_amount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment Info */}
              <div className="text-sm text-gray-500">
                <p>Payment Method: {selectedInvoice.payment_method}</p>
                <p>Order ID: {selectedInvoice.order_id}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySubscriptionPage;
